import { useState, useDeferredValue } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { api } from "@/lib/api-client";
import { Button } from "@/components/ui/Button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import {
  AssessmentTable,
  type AssessmentRow,
} from "@/components/assessments/AssessmentTable";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { CreateAssessmentDialog } from "@/components/assessments/CreateAssessmentDialog";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuthStore } from "@/store/auth.store";
import { userCan } from "@/lib/permissions";
import { toast } from "@/components/ui/Toast";
import type { AssessmentStatus } from "@/types";

// ─────────────────────────────────────────────
// AssessmentsList
//
// Screen 5 — /assessments
// Tabs: All / Draft / Submitted / In Review / Approved / Rejected
// Design: §8.5 Screen 5: Assessments List
// ─────────────────────────────────────────────

type TabFilter = "ALL" | AssessmentStatus;

interface AssessmentApiRow {
  id: string;
  entityId: string;
  status: AssessmentStatus;
  currentVersion: number;
  assignedTo: string | null;
  assignedToName?: string | null;
  updatedAt: string;
  entity?: { id: string; name: string };
  schemaVersion?: { version: number; schema?: { name: string } };
  responseSummary?: { total: number; answered: number; completionPct: number };
  latestScore?: { overallScore: number } | null;
}

interface AssessmentsListResponse {
  data: AssessmentApiRow[];
  meta: { total: number; page: number; pageSize: number; totalPages: number };
}

export function AssessmentsList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const canDelete = userCan(user, "delete", "Assessment");

  const [tab, setTab] = useState<TabFilter>("ALL");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/assessments/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assessments"] });
      setPendingDeleteId(null);
      toast.success("Assessment deleted");
    },
    onError: (err: Error) => {
      setPendingDeleteId(null);
      toast.error(err.message ?? "Failed to delete assessment");
    },
  });

  const deferredSearch = useDeferredValue(search);

  const TABS: { value: TabFilter; label: string }[] = [
    { value: "ALL", label: t("assessment.all") },
    { value: "DRAFT", label: t("status.draft") },
    { value: "SUBMITTED", label: t("status.submitted") },
    { value: "IN_REVIEW", label: t("status.inReview") },
    { value: "APPROVED", label: t("status.approved") },
    { value: "REJECTED", label: t("status.rejected") },
  ];

  const params = new URLSearchParams({ page: String(page), pageSize: "20" });
  if (tab !== "ALL") params.set("status", tab);
  if (deferredSearch) params.set("search", deferredSearch);

  const { data, isLoading, isError, refetch } = useQuery<AssessmentsListResponse>({
    queryKey: ["assessments", tab, deferredSearch, page],
    queryFn: () => api.get(`/assessments?${params.toString()}`),
  });

  const rows: AssessmentRow[] = (data?.data ?? []).map((a) => ({
    id: a.id,
    entityId: a.entityId,
    entityName: a.entity?.name ?? a.entityId,
    schemaName: a.schemaVersion?.schema?.name ?? `v${a.schemaVersion?.version ?? 1}`,
    status: a.status,
    assignedTo: a.assignedTo,
    assignedToName: a.assignedToName ?? null,
    completionPct: a.responseSummary?.completionPct ?? 0,
    answered: a.responseSummary?.answered ?? 0,
    total: a.responseSummary?.total ?? 0,
    overallScore: a.latestScore?.overallScore ?? null,
    updatedAt: a.updatedAt,
    version: a.currentVersion,
  }));

  function handleTabChange(v: string) {
    setTab(v as TabFilter);
    setPage(1);
  }

  function handleSearchChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSearch(e.target.value);
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-text-primary">{t("assessment.title")}</h1>
          <p className="mt-1 text-[14px] text-text-secondary">
            {t("assessment.subtitle")}
          </p>
        </div>
        <Button
          leadingIcon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          {t("assessment.newAssessment")}
        </Button>
      </div>

      {/* Filter row — search on the left, status tabs on the right */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder={t("assessment.searchPlaceholder")}
            value={search}
            onChange={handleSearchChange}
            className="h-9 w-full rounded-control border border-border-default bg-bg-input pl-9 pr-3 text-[14px] text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:border-accent focus:ring-accent"
          />
        </div>
        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList>
            {TABS.map((tabItem) => (
              <TabsTrigger key={tabItem.value} value={tabItem.value}>
                {tabItem.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {/* Table */}
      <AssessmentTable
        data={rows}
        isLoading={isLoading}
        isError={isError}
        onRetry={() => refetch()}
        page={page}
        totalPages={data?.meta.totalPages}
        totalItems={data?.meta.total}
        pageSize={20}
        onPageChange={setPage}
        canDelete={canDelete}
        onDelete={(row) => setPendingDeleteId(row.id)}
      />

      {/* Create dialog */}
      <CreateAssessmentDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => navigate(`/assessments/${id}`)}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={pendingDeleteId !== null}
        onOpenChange={(open) => { if (!open) setPendingDeleteId(null); }}
        title="Delete Assessment"
        description="This will permanently delete the assessment along with all its responses, versions, scores, and vendor invitations. This action cannot be undone."
        confirmLabel="Delete"
        isDestructive
        isLoading={deleteMutation.isPending}
        onConfirm={() => { if (pendingDeleteId) deleteMutation.mutate(pendingDeleteId); }}
      />
    </div>
  );
}
